import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';

const ERROR_NO_ENCONTRADO = 'no_encontrado';
const ERROR_VALIDACION = 'validacion';
const ERROR_INESPERADO = 'inesperado';

const MOTIVO_IDENTIFICACION_INVALIDA = 'identificacion_invalida';

const IDENTIFICACION_INVALIDA_ERROR = 'IDENTIFICACION_INVALIDA';

/**
 * Composable: useVentaPrivadaAbonos
 *
 * Owns the venta-privada identification state for the public Normal alta-de-abonos wizard.
 *
 * Responsibilities:
 *  - Detect whether the current theatre has `venta_privada_habilitada` on `lista_abonados`.
 *  - Submit the `POST /f/abonados/identificar-inicial` request and surface the referente.
 *  - Map Axios error responses to typed error codes (`no_encontrado`, `validacion`, `inesperado`).
 *  - Augment `bloquear-asientos` payloads with `identificacion_*` fields when identification is valid.
 *  - Detect `motivo=identificacion_invalida` from the bloquear rejection and throw
 *    a typed `Error('IDENTIFICACION_INVALIDA')` so the host can react to it
 *    (the wizard listens for the `identificacion-invalida` event and resets seats).
 *  - Skip identification when the URL carries renewal query params (`n_abonado_antiguo` + `dni`);
 *    the renewal flow already validated identity via `buscarAbonado` before issuing the link,
 *    so no user interaction or `identificacion_*` injection is required.
 *
 * PII discipline: `dni` is held ONLY in the in-memory `dni_input` ref. The composable
 * MUST NOT persist `dni` to localStorage, sessionStorage, or cookies.
 *
 * @param {Object} options
 * @param {Function} [options.getQueryParams] - Returns the current route query params object.
 *                                               When both `n_abonado_antiguo` and `dni` are present,
 *                                               identification is skipped (renewal flow).
 */
export function useVentaPrivadaAbonos(options = {}) {
    const { getQueryParams = () => ({}) } = options;
    const loading = ref(false);
    const error = ref(null);
    const errorsValidacion = ref({});
    const referente = ref(null);

    const n_abonado_input = ref('');
    const dni_input = ref('');

    const requiereIdentificacion = computed(() => {
        const lista = store.getters.getListaAbonados || {};
        if (!lista.venta_privada_habilitada) return false;

        // Renewal flow — skip identification; see JSDoc above.
        const query = getQueryParams();
        if (query.n_abonado_antiguo != null && query.dni) return false;

        return true;
    });

    const identificacionValida = computed(() => referente.value !== null);

    const identificacionPayload = computed(() => {
        if (!referente.value) return null;
        return {
            identificacion_n_abonado: n_abonado_input.value,
            identificacion_dni: dni_input.value
        };
    });

    function resolveErrorCode(err) {
        const status = err?.response?.status;
        if (status === 404) return ERROR_NO_ENCONTRADO;
        if (status === 422) return ERROR_VALIDACION;
        return ERROR_INESPERADO;
    }

    async function identificarInicial() {
        if (!requiereIdentificacion.value) {
            // No-op when flag is off — guarantees byte-identical behaviour.
            return;
        }

        // Client-side input validation: short-circuit when either field is empty.
        // This avoids an unnecessary round-trip and matches the same shape the
        // server returns for 422 validation, so the UI can show inline errors
        // without distinguishing client vs server origin.
        const nEmpty = !n_abonado_input.value || n_abonado_input.value.trim() === '';
        const dEmpty = !dni_input.value || dni_input.value.trim() === '';
        if (nEmpty || dEmpty) {
            error.value = ERROR_VALIDACION;
            errorsValidacion.value = {};
            if (nEmpty) {
                errorsValidacion.value.n_abonado = ['El campo nº de abonado es obligatorio'];
            }
            if (dEmpty) {
                errorsValidacion.value.dni = ['El campo DNI es obligatorio'];
            }
            referente.value = null;
            loading.value = false;
            return;
        }

        loading.value = true;
        error.value = null;
        errorsValidacion.value = {};

        try {
            const response = await store.dispatch(config.getVuexAlias('front.abonados.identificar.inicial'), {
                n_abonado: n_abonado_input.value,
                dni: dni_input.value
            });

            // Response shape: { data: { id, n_abonado, name, surnames } }
            // The Axios layer wraps it as { data: { data: {...} } } per api.call conventions.
            const body = response?.data?.data ?? response?.data ?? null;
            if (!body) {
                error.value = ERROR_INESPERADO;
                return;
            }

            referente.value = {
                id: body.id,
                n_abonado: body.n_abonado,
                name: body.name,
                surnames: body.surnames
            };
            error.value = null;
        } catch (err) {
            error.value = resolveErrorCode(err);
            if (error.value === ERROR_VALIDACION) {
                const fieldErrors = err?.response?.data?.errors;
                if (fieldErrors && typeof fieldErrors === 'object') {
                    errorsValidacion.value = { ...fieldErrors };
                }
            }
            referente.value = null;
        } finally {
            loading.value = false;
        }
    }

    async function bloquearConIdentificacion(payload) {
        const requiresIdent = requiereIdentificacion.value && identificacionValida.value;

        const enriched = requiresIdent
            ? {
                ...payload,
                identificacion_n_abonado: n_abonado_input.value,
                identificacion_dni: dni_input.value
            }
            : payload;

        try {
            return await store.dispatch(config.getVuexAlias('front.bloquear.asientos'), enriched);
        } catch (err) {
            const motivo = err?.response?.data?.data?.motivo;
            if (err?.response?.status === 422 && motivo === MOTIVO_IDENTIFICACION_INVALIDA) {
                // The host owns the seat reset (reacts to the typed error /
                // `identificacion-invalida` event). This composable only resets
                // its own identification state.
                limpiarIdentificacion();
                throw new Error(IDENTIFICACION_INVALIDA_ERROR);
            }
            throw err;
        }
    }

    function limpiarIdentificacion() {
        referente.value = null;
        n_abonado_input.value = '';
        dni_input.value = '';
        error.value = null;
        errorsValidacion.value = {};
    }

    return {
        loading,
        error,
        errorsValidacion,
        referente,
        n_abonado_input,
        dni_input,
        requiereIdentificacion,
        identificacionValida,
        identificacionPayload,
        identificarInicial,
        bloquearConIdentificacion,
        limpiarIdentificacion
    };
}